import axios from 'axios'

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024
const s3UploadClient = axios.create()

export const updateFile = async ({ id, file }) => {
  const response = await axios.patch(
    `/files/${id}`,
    {
      file: file,
    },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return response.data.data
}

export const getFile = async ({ id }) => {
  const response = await axios.get(`/files/${id}`)
  return response.data.data
}

export const deleteFile = async ({ id, logCatalog = undefined, givenEntityId = undefined }) => {
  const response = await axios.delete(`/files/${id}`, {
    data: {
      logCatalog: logCatalog,
      givenEntityId: givenEntityId,
    },
  })
  return response.data.data
}

export const uploadBulkFiles = async (files, options = {}) => {
  const filesToUpload = (files || []).filter(Boolean)
  if (!filesToUpload.length) return []

  const oversizedFile = filesToUpload.find((file) => Number(file?.size || 0) > MAX_IMAGE_SIZE_BYTES)
  if (oversizedFile) {
    throw new Error(`File "${oversizedFile.name}" exceeds the 5MB size limit`)
  }

  const fileDescriptors = filesToUpload.map((file) => ({
    filename: file.name,
    mimetype: file.type || 'application/octet-stream',
    encoding: '7bit',
    size: Number(file.size || 0),
  }))

  const presignedUploadsResponse = await axios.post('/files/bulk/presign', {
    files: fileDescriptors,
  })

  const presignedUploads = presignedUploadsResponse?.data?.data || []
  const { onProgress } = options
  const bytesByFile = filesToUpload.map(() => 0)
  const totalBytes = filesToUpload.reduce((sum, file) => sum + Number(file?.size || 0), 0)
  const totalBytesSafe = totalBytes > 0 ? totalBytes : filesToUpload.length

  await Promise.all(
    presignedUploads.map((presignedUpload, index) =>
      // Use a clean client so API auth interceptors don't add Authorization headers to S3 requests.
      s3UploadClient.put(presignedUpload.uploadUrl, filesToUpload[index], {
        headers: {
          'Content-Type':
            filesToUpload[index].type || presignedUpload.mimetype || 'application/octet-stream',
        },
        onUploadProgress: (event) => {
          const loaded = Number(event?.loaded || 0)
          bytesByFile[index] = loaded
          const uploadedBytes = bytesByFile.reduce((sum, bytes) => sum + bytes, 0)
          const progress = Math.min(100, Math.round((uploadedBytes / totalBytesSafe) * 100))
          if (typeof onProgress === 'function') {
            onProgress(progress)
          }
        },
      }),
    ),
  )

  const confirmedUploadPayload = {
    files: presignedUploads.map(({ filename, mimetype, encoding, key }) => ({
      filename,
      mimetype,
      encoding,
      key,
    })),
  }

  const confirmedFilesResponse = await axios.post('/files/bulk/confirm', confirmedUploadPayload)

  return confirmedFilesResponse.data.data
}
